import { useCallback, useContext, useMemo, useState } from 'react';
import { UserContext } from 'shared/Providers/User';
import { useUserData } from 'shared/Providers/UserData';
import { useDispatch } from 'react-redux';
import { updateUserProperties } from 'shared/modules/user/actions';
import { doFetchUserProperties } from 'shared/new/authUtils';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Chip,
	styled,
	SxProps,
	Theme,
	Typography,
} from '@mui/material';
import UserAvatar from 'shared/Pages/common/Header/AccountMenu/avatar';
import { prettyDate } from 'shared/utils/format';
import { FieldTextField, FormTextField } from 'shared/_ui/form/form-text-field';
import { IconUser } from 'shared/_ui/icon';
import { CardHeaderWithActions } from 'shared/_pages/account/styles';
import { TUser } from 'shared/model/user-types';
import { FieldsContainer } from 'shared/_ui/layout';

type TAccount = {
	name: string;
	email: string;
};

export const UserTitleContainer = styled(Box, { name: 'UserTitleContainer' })(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(4),
	mb: theme.spacing(6),
}));

export const UserActionsStyled = styled(CardActions, { name: 'UserActionsStyled' })(
	({ theme }) => ({
		justifyContent: 'end',
		p: theme.spacing(4),
		pt: theme.spacing(2),
	})
);

function UserTitle({ user }: { user?: TUser }) {
	const userData = useUserData();
	const mainRole = user?.roles?.at(0)?.name;
	const info = `${mainRole} since ${prettyDate(userData.user.created)}`;

	return (
		<UserTitleContainer>
			<UserAvatar name={user?.name} sx={{ width: 52, height: 52 }} />
			<Box>
				<Typography
					variant="h4"
					component="div"
					sx={{
						textAlign: 'left',
					}}
				>
					{user?.name}
				</Typography>
				<Typography
					color="secondary"
					sx={{
						m: 0,
					}}
				>
					{info}
				</Typography>
			</Box>
		</UserTitleContainer>
	);
}

export default function UserCard() {
	const {
		permissions: { canUseApp },
	} = useContext(UserContext);

	const { user } = useContext(UserContext);
	const { name: userName = '', email: userEmail = '' } = user ?? {};

	const dispatch = useDispatch();

	const [edit, setEdit] = useState(false);
	const editMode = useMemo(() => edit && canUseApp, [edit, canUseApp]);

	const toggleEdit = useCallback(
		(resetForm?: () => void) => {
			if (edit && resetForm) {
				resetForm();
			}
			setEdit(!edit);
		},
		[edit]
	);

	const initialValues = useMemo(
		() => ({ name: userName, email: userEmail }),
		[userName, userEmail]
	);

	const handleSubmit = useCallback(
		async (values: TAccount) => {
			if (userName !== values.name || userEmail !== values.email) {
				await updateUserProperties(values);
				await dispatch(doFetchUserProperties());
			}

			toggleEdit();
		},
		[userName, userEmail, dispatch, toggleEdit]
	);

	const formik = useFormik<TAccount>({
		initialValues,
		validationSchema: object().shape({
			name: string().required('Required'),
			email: string().email('Invalid email address').required('Required'),
		}),
		enableReinitialize: true,
		onSubmit: handleSubmit,
	});

	const fieldSx: SxProps<Theme> = {
		'.MuiInputBase-input': {
			textAlign: !editMode ? 'right' : undefined,
			fontWeight: 600,
		},
		'.MuiTypography-root': {
			textAlign: 'right',
			fontWeight: 600,
		},
	};

	const action = !canUseApp ? undefined : (
		<>
			{!editMode && <Button onClick={() => toggleEdit(formik.resetForm)}>Edit</Button>}
			{editMode && (
				<Button color="secondary" onClick={() => toggleEdit(formik.resetForm)}>
					Cancel
				</Button>
			)}
		</>
	);

	return (
		<Card sx={{ flex: 1 }}>
			<form onSubmit={formik.handleSubmit}>
				<CardHeaderWithActions title="Profile Information" action={action} avatar={<IconUser />} />
				<CardContent>
					<UserTitle user={user} />
					<FieldsContainer>
						<FormTextField
							label="Full name:"
							name="name"
							fullWidth
							formik={formik}
							sx={fieldSx}
							labelProps={{ width: '4.5rem' }}
							spellCheck
							readOnly={!editMode}
						/>
						<FormTextField
							label="Email:"
							name="email"
							fullWidth
							formik={formik}
							sx={fieldSx}
							labelProps={{ width: '4.5rem' }}
							readOnly={!editMode}
						/>
						<FieldTextField
							label="Role:"
							value={
								<>
									{user?.roles?.map(({ name }) => (
										<Chip key={name} label={name} sx={{ backgroundColor: '#fef3c7' }} />
									))}
								</>
							}
							fullWidth
							labelProps={{ width: '2.5rem' }}
							sx={fieldSx}
						/>
						<FieldTextField
							label="Account Status:"
							value={
								<Box component="span" sx={(theme) => ({ color: theme.palette.success.main })}>
									Active
								</Box>
							}
							fullWidth
							labelProps={{ width: '6.2rem' }}
							sx={fieldSx}
						/>
					</FieldsContainer>
				</CardContent>
				{editMode && (
					<UserActionsStyled>
						<Button color="primary" type="submit">
							Save
						</Button>
					</UserActionsStyled>
				)}
			</form>
		</Card>
	);
}
